import { CSSProperties, useLayoutEffect, useRef, useState } from "react";

type LazyShimmerImageProps = {
  src?: string | null;
  alt?: string;
  className?: string;
  style?: CSSProperties;
};

/** Image with a shimmer background while loading. */
const LazyShimmerImage = ({
  src,
  alt = "",
  className = "",
  style,
}: LazyShimmerImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const trimmedSrc = typeof src === "string" ? src.trim() : "";

  // Cached images often fire onLoad before React attaches the handler (or before
  // a "reset loaded" effect runs). Check `complete` so shimmer does not stick.
  useLayoutEffect(() => {
    setLoaded(false);
    if (!trimmedSrc) return;

    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [trimmedSrc]);

  return (
    <div
      className={`lazyShimmerImage ${loaded ? "is-loaded" : "is-shimmer"}${
        className ? ` ${className}` : ""
      }`}
      style={style}
    >
      {trimmedSrc ? (
        <img
          ref={imgRef}
          src={trimmedSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          draggable={false}
          className={`lazyShimmerImage__img${loaded ? " is-visible" : ""}`}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
      ) : null}
    </div>
  );
};

export default LazyShimmerImage;
